import { FC, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Navigate } from 'react-router';

import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { PATHS } from '@/shared/config';
import { useAppSelector, useMedia } from '@/shared/hooks';
import { Layout, MobileLayout, toastCaller } from '@/shared/ui';

import { organizationModel } from '@/entities/organization';

import { CommunityCatalogPane } from '@/features/communityCatalog';
import { Footer } from '@/features/footer';

import { Header, MobileHeader } from '@/widgets/Header';

type CommunityProps = {
  className?: string;
};

export const Community: FC<CommunityProps> = (props) => {
  const { className } = props;
  const { formatMessage } = useIntl();
  const { isMobile } = useMedia();

  const isConsultationTemplatesEnabled = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Enable_ConsultationTemplates,
    ),
  );
  const currentOrganization = useAppSelector(
    organizationModel.selectors.selectCurrentOrganization,
  );

  const areFeatureFlagsReady = currentOrganization?.FeatureFlags !== undefined;
  const isAccessDenied =
    areFeatureFlagsReady && !isConsultationTemplatesEnabled;

  useEffect(() => {
    if (!isAccessDenied) {
      return;
    }

    toastCaller({
      type: 'warning',
      heading: formatMessage({
        id: 'global.accessDenied',
        defaultMessage: 'Access denied',
      }),
      message: formatMessage({
        id: 'community.accessDenied.message',
        defaultMessage:
          'The Community page is not enabled for your organization.',
      }),
    });
  }, [isAccessDenied, formatMessage]);

  if (!areFeatureFlagsReady) {
    return null;
  }

  if (isAccessDenied) {
    return <Navigate replace to={PATHS.main} />;
  }

  if (isMobile) {
    return (
      <MobileLayout>
        <MobileHeader hideBreadcrumbs />
        <MobileLayout.Main>
          <CommunityCatalogPane className={className} />
        </MobileLayout.Main>
      </MobileLayout>
    );
  }

  return (
    <Layout>
      <Layout.Header>
        <Header />
      </Layout.Header>
      <Layout.Content data-section="community-catalog">
        <Layout.Main>
          <CommunityCatalogPane className={className} />
        </Layout.Main>
      </Layout.Content>
      <Layout.Footer>
        <Footer />
      </Layout.Footer>
    </Layout>
  );
};
